import HelpIcon from '@mui/icons-material/Help';
import { Stack } from '@mui/material';
import 'driver.js/dist/driver.css';
import { type Theme, useMediaQuery } from '@mui/material';
import { useCallback, useState } from 'react';
import MobileHeader from '/@/components/bayer/header/mobile-header';
import PageContainer from '/@/components/bayer/page-container';
import { useOwnUserNode } from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import Header from '../../../../components/bayer/header';
import GrowerDashboard from '../../grower/components/dashboard';
import NewFieldGrowersModal from '../../growers/components/new-field-modal';
import { useTabs } from '../../index';
import { MY_FIELDS_TOUR_IDS } from './consultant-fields-lots-tour-ids';
import MyFieldsHelpTourDriver from './my-fields-help-tour/MyFieldsHelpTourDriver';
import { useMyFieldsHelpTour } from './my-fields-help-tour/useMyFieldsHelpTour';

const MyFieldsListDashboard = () => {
    const userNodeQuery = useOwnUserNode();
    const consultantNode = userNodeQuery.data;
    const isConsultant = useIsValidatedConsultant();
    const [newFieldModalOpen, setNewFieldModalOpen] = useState(false);
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const { tab, handleTabChange } = useTabs();

    const isRv = useIsValidatedRV();
    const isAdvisor = useIsValidatedAdvisor();
    const showFieldsTour = isRv || isAdvisor || isConsultant;

    const ensureCurrentCampaignTab = useCallback(() => {
        handleTabChange('current-campaign');
    }, [handleTabChange]);

    const { fieldsTourSteps, prepareFieldsTour, startHelpTourFromIcon } = useMyFieldsHelpTour({
        showFieldsTour,
        onEnsureCurrentCampaignTab: ensureCurrentCampaignTab,
        canStartManualTour: Boolean(showFieldsTour && consultantNode?.id),
    });

    return (
        <PageContainer
            header={
                <Stack id={MY_FIELDS_TOUR_IDS.INTRO} spacing={1} sx={{ mt: isSmOrLower ? 0 : 4 }}>
                    {isSmOrLower ? (
                        <MobileHeader title={_('Mis Lotes')} />
                    ) : (
                        <Header
                            title={_('Mis Lotes')}
                            subtitle={_(
                                'Este es un espacio personal para crear y gestionar lotes, hacer seguimiento, generar recomendaciones y analizar resultados de campaña.'
                            )}
                            showSkeleton={userNodeQuery.isValidating}
                            titleIcon={
                                showFieldsTour ? (
                                    <HelpIcon
                                        color='primary'
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            cursor: consultantNode?.id ? 'pointer' : 'default',
                                        }}
                                        onClick={startHelpTourFromIcon}
                                    />
                                ) : undefined
                            }
                        />
                    )}
                </Stack>
            }
        >
            {showFieldsTour && (
                <MyFieldsHelpTourDriver
                    steps={fieldsTourSteps}
                    isLoading={userNodeQuery.isValidating || !consultantNode}
                    onBeforeAutoStart={prepareFieldsTour}
                />
            )}
            <Stack spacing={4}>
                <Stack mt={showFieldsTour ? 2 : 4}>
                    {consultantNode?.id && (
                        <GrowerDashboard
                            growerNodeId={consultantNode.id}
                            tab={tab}
                            handleTabChange={handleTabChange}
                            showNewFieldButton={true}
                            fieldsTourAnchors={showFieldsTour}
                        />
                    )}
                </Stack>
                <NewFieldGrowersModal
                    open={newFieldModalOpen}
                    setOpen={setNewFieldModalOpen}
                    growerUserNodeId={consultantNode?.id}
                    trackEntrySource='grower-dashboard-card'
                />
            </Stack>
        </PageContainer>
    );
};

export default MyFieldsListDashboard;
